import React, { FC, PropsWithChildren, useState } from "react";
import {
  CreateTradeSections,
  TradingTerminalTabs,
} from "@shared/features/tradingTerminal/common/types";
import { TradingTerminalContext } from "./TradingTerminal.context";
import useTradeFormValues from "@shared/features/tradingTerminal/common/hooks/useTradeFormValues";
import useAmount from "@shared/features/tradingTerminal/common/hooks/useAmount";
import useTradingTerminalQueries from "@shared/features/tradingTerminal/common/hooks/useTradingTerminalQueries";
import useErrors from "@shared/features/tradingTerminal/common/hooks/useErrors";
import { selectWorkspace } from "@shared/slices/common.slice";
import { WorkspaceType } from "@shared/types";
import { useSelector } from "react-redux";

interface TradeFormProviderProps {
  isMobile: boolean;
  isSignal: boolean;
}

const TradingTerminalProvider: FC<
  PropsWithChildren<TradeFormProviderProps>
> = ({ isMobile, isSignal, children }) => {
  const [useTradingTerminalOldVersion, setUseTradingTerminalOldVersion] =
    useState(false);
  const [tab, setTab] = useState<TradingTerminalTabs>(
    TradingTerminalTabs.SMART_TRADE
  );
  const [createTradeTab, setCreateTradeTab] = useState(
    CreateTradeSections.GENERAL
  );

  const workspace = useSelector(selectWorkspace);
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const { formSetters, formValues } = useTradeFormValues(isSignal);
  const { account, symbols, extraInfo, exchanges, subGroupIds } = formValues;

  const {
    getCreateTradeGeneralDataQuery,
    getSignalCreateGeneralDataQuery,
    getAvailableQuantityQuery,
    createTrade,
    createTradeLoading,
    createSignal,
    createSignalLoading,
    isLoading,
    isFetching,
    freeTextCreateTrade,
    freeTextCreateTradeLoading,
    isError,
    refetchErrorQueries,
    getGroupsDataQuery,
    getAdminSubGroupsStatus,
  } = useTradingTerminalQueries({
    account,
    exchanges,
    symbol: symbols?.[0],
    reverse: extraInfo?.reverse,
    subGroupIds,
    isSignal,
    isAdminWorkspace,
  });
  const getCreateGeneralDataQuery = isSignal
    ? getSignalCreateGeneralDataQuery
    : getCreateTradeGeneralDataQuery;
  const currentPrice =
    getCreateTradeGeneralDataQuery?.data?.data.orders.current_price ??
    getSignalCreateGeneralDataQuery?.data?.data.targets.current_price;

  const amountData = useAmount({
    formValues,
    formSetters,
    isSignal,
    isLoading,
    getCreateGeneralDataQuery,
    getAvailableQuantityQuery,
    currentPrice: currentPrice || 0,
    isAdminWorkspace,
  });

  const errorsData = useErrors({
    formValues,
    amountData,
    getCreateGeneralDataQuery,
    isSignal,
    currentPrice,
  });

  return (
    <TradingTerminalContext.Provider
      value={{
        useTradingTerminalOldVersion,
        setUseTradingTerminalOldVersion,
        isMobile,
        tab,
        setTab,
        createTradeTab,
        setCreateTradeTab,
        currentPrice,
        getCreateTradeGeneralDataQuery,
        getSignalCreateGeneralDataQuery,
        getCreateGeneralDataQuery,
        getAvailableQuantityQuery,
        getGroupsDataQuery,
        getAdminSubGroupsStatus,
        freeTextCreateTrade,
        freeTextCreateTradeLoading,
        createTrade,
        createTradeLoading,
        createSignal,
        createSignalLoading,
        isLoading,
        isFetching,
        isError,
        isSignal,
        refetchErrorQueries,
        ...formValues,
        ...formSetters,
        ...amountData,
        ...errorsData,
      }}
    >
      {children}
    </TradingTerminalContext.Provider>
  );
};

export default TradingTerminalProvider;
